import { CommonModule } from '@angular/common';
import {
  Component, Input, Output, EventEmitter,
  signal, computed, effect,
  OnInit,
  Renderer2,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigInput } from '../../interfaces/interfaces';
import { hasPreloadedValue as fieldHasValue, normalizeFieldValue, resolveFieldEnabled } from '../utils/field-preload.utils';
import { LockToggleComponent } from '../lock-toggle/lock-toggle.component';
import { FieldErrorTimeoutService } from '../../services/field-error-timeout.service';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, LockToggleComponent],
  templateUrl: './input.component.html',
  styleUrl: './input.component.css',
})
export class InputComponent implements OnInit, AfterViewInit, OnDestroy {

  private lockTouched = false;

  @Input() set config(value: ConfigInput) {
    const showBlocker = value.showBlocker ?? false;
    this.configSignal.set({
      label: value.label || 'Item',
      subLabel: value.subLabel || '',
      formName: value.formName,
      placeholder: value.placeholder || 'Ingrese un valor',
      type: value.type || 'text',
      defaultValue: value.defaultValue ?? '',
      enabled: resolveFieldEnabled({
        showBlocker,
        lockTouched: this.lockTouched,
        currentEnabled: this.configSignal().enabled,
        requestedEnabled: value.enabled,
        defaultValue: value.defaultValue,
        editedByUser: this.editedByUser,
      }),
      validation: value.validation || null,
      textError: value.textError || 'Este campo es requerido.',
      showBlocker,
      originalValue: value.originalValue ?? value.defaultValue ?? null,
      isDirty: value.isDirty ?? false,
      isValid: value.isValid ?? true,
    });
  }

  @Input() timeoutSeconds: number = 8;

  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;

  configSignal = signal<ConfigInput>({
    label: 'Item',
    subLabel: '',
    placeholder: 'Ingrese un valor',
    type: 'text',
    defaultValue: null,
    enabled: true,
    validation: null,
    textError: 'Este campo es requerido.',
    showBlocker: false,
    originalValue: null,
    isDirty: false,
    isValid: true,
  });

  formattedValue = signal<string>('');

  enabled = computed(() => this.configSignal().enabled);
  currentValue = computed(() => {
    const config = this.configSignal();
    return normalizeFieldValue(config.defaultValue, config.type);
  });
  showBlockerIcon = computed(() => this.configSignal().showBlocker === true);
  showInputError = computed(() => {
    const control = this.form?.get('item');
    return this.enabled() && (this.offTimeOut() || (!!control?.invalid && !!control?.touched));
  });

  emitOnlyOnBlur = computed(() => {
    const formName: any = this.configSignal().formName;
    const blurOnlyFields = ['income', 'address'];
    return blurOnlyFields.includes(formName);
  });

  form: FormGroup;

  offTimeOut = signal(false);
  hasWritten = signal(false);
  valueSignal = signal<any>('');
  lastEmittedValue = signal<any>(null);

  hasSpecificError = computed(() => {
    const c = this.itemControl();
    if (!c || !c.errors) return false;
    return (
      c.hasError('maxDateYesterdayColombia') ||
      c.hasError('maxDateTodayColombia') ||
      c.hasError('min') ||
      c.hasError('max')
    );
  });

  hasGenericError = computed(() => {
    const c = this.itemControl();
    if (!c || !c.errors) return false;
    return (
      c.hasError('required') ||
      c.hasError('minlength') ||
      c.hasError('maxlength') ||
      c.hasError('pattern')
    );
  });

  private viewInitialized = signal(false);
  private debounceTimer: any;
  private timeoutHandle: any;
  private isUpdating = false;
  private editedByUser = false;

  @Output() submitValue = new EventEmitter<any>();
  @Output() dirtyChange = new EventEmitter<{ formName: string, isDirty: boolean, isValid: boolean }>();
  @Output() fieldStateChange = new EventEmitter<{ formName?: string; value: string | number | null; valid: boolean }>();
  @Output() enabledChange = new EventEmitter<{ formName: string; enabled: boolean }>();

  constructor(
    private renderer: Renderer2,
    private fieldErrorTimeout: FieldErrorTimeoutService
  ) {
    this.form = new FormGroup({
      item: new FormControl('', Validators.compose(this.configSignal().validation ?? []))
    });

    /* Habilitar / deshabilitar dinámicamente */
    effect(() => {
      const control = this.form.get('item');
      const isEnabled = this.enabled();
      if (isEnabled) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }

      if (this.viewInitialized()) {
        setTimeout(() => this.applyInitialStyles(), 0);
      }
    });

    /* Cambiar validaciones dinámicamente */
    effect(() => {
      const control = this.form.get('item');
      const validations = this.configSignal().validation;
      control?.setValidators(Validators.compose(validations ?? []));
      control?.updateValueAndValidity();
    });

    /* Setear valor por defecto */
    effect(() => {
      this.applyDefaultValue(this.currentValue());
    });

    /* Cambios de valor */
    this.form.get('item')?.valueChanges.subscribe((val) => {
      if (this.isUpdating) return;

      const control = this.form.get('item');
      const isValid = control ? control.valid : false;
      const formName = this.configSignal().formName || '';

      if (this.configSignal().type === 'number' && val !== null && val !== '') {
        const numericValue = String(val).replace(/[.,\s]/g, '');
        if (/^\d+$/.test(numericValue)) {
          this.formattedValue.set(this.formatNumberWithCommas(numericValue));
          this.valueSignal.set(numericValue);
          if (String(val) !== numericValue) {
            this.form.get('item')?.setValue(numericValue, { emitEvent: false });
          }
          this.emitFieldStateChange();
        }
      } else {
        this.valueSignal.set(val);
        this.formattedValue.set(val);
      }

      if (val !== '' && val !== null && val !== undefined) {
        this.hasWritten.set(true);
      }

      this.startTimeout();

      if (!this.emitOnlyOnBlur()) {
        this.resetDebounce();
      }

      this.updateStylesByState();
      this.emitFieldStateChange();

      if (this.emitOnlyOnBlur()) {
        return;
      }

      const originalValue = this.configSignal().originalValue;
      const isDirty = val !== originalValue && val !== '' && val !== null && val !== undefined;

      this.submitValue.emit({
        formName: formName,
        value: val,
        isValid: isValid,
        isDirty: isDirty
      });

      if (isDirty !== this.configSignal().isDirty) {
        this.configSignal.update(config => ({
          ...config,
          isDirty: isDirty,
          isValid: isValid
        }));
        this.dirtyChange.emit({ formName, isDirty, isValid });
      }
    });

    /* Si se vuelve válido: apagar timeout y errores */
    effect(() => {
      const valid = this.itemControl()?.valid;
      if (valid && this.enabled()) {
        this.offTimeOut.set(false);
        clearTimeout(this.timeoutHandle);
        this.getStyleEnabled();
      }
    });
  }

  toggleEnabled() {
    this.lockTouched = true;
    const enabled = !this.configSignal().enabled;
    this.configSignal.update((config) => ({
      ...config,
      enabled
    }));

    if (this.configSignal().formName) {
      this.enabledChange.emit({ formName: this.configSignal().formName!, enabled });
    }

    setTimeout(() => {
      const control = this.form.get('item');
      if (this.enabled() && control?.valid) {
        this.emit();
      }
    });
  }

  ngOnInit() {
    this.startTimeout();
  }

  private applyDefaultValue(newValue: unknown): void {
    const control = this.form.get('item');
    if (!control) {
      return;
    }

    const type = this.configSignal().type;
    const incomingNormalized = normalizeFieldValue(newValue, type);
    const originalNormalized = normalizeFieldValue(this.configSignal().originalValue, type);
    if (this.editedByUser && incomingNormalized == originalNormalized) {
      return;
    }

    const incomingEmpty = !fieldHasValue(newValue) && newValue !== 0 && newValue !== false;
    const currentEmpty = !fieldHasValue(control.value) && control.value !== 0 && control.value !== false;

    if (incomingEmpty && currentEmpty) {
      return;
    }

    if (!incomingEmpty && control.value == newValue) {
      return;
    }

    this.isUpdating = true;
    control.setValue(incomingEmpty ? '' : newValue, { emitEvent: false });

    if (this.configSignal().type === 'number') {
      const numeric = incomingEmpty ? '' : String(newValue).replace(/,/g, '');
      this.formattedValue.set(numeric ? this.formatNumberWithCommas(numeric) : '');
      this.valueSignal.set(numeric);
    } else {
      this.valueSignal.set(incomingEmpty ? '' : newValue);
      this.formattedValue.set(incomingEmpty ? '' : String(newValue ?? ''));
    }

    if (incomingEmpty) {
      this.hasWritten.set(false);
    }

    setTimeout(() => {
      this.isUpdating = false;
    }, 50);
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();
    setTimeout(() => {
      const control = this.form.get('item');
      const configValue = this.configSignal().defaultValue;
      const value = fieldHasValue(control?.value) ? control?.value : configValue;

      if (!fieldHasValue(value)) {
        return;
      }

      const formName = this.configSignal().formName;
      if (!formName) {
        return;
      }

      this.submitValue.emit({
        formName,
        value,
        isValid: true,
        isDirty: false
      });
    }, 500);
  }

  ngOnDestroy(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    if (this.timeoutHandle) clearTimeout(this.timeoutHandle);
  }

  itemControl: any = computed(() => this.form.get('item'));

  private applyInitialStyles() {
    if (this.enabled()) {
      this.getStyleEnabled();
    } else {
      this.getStyleTextDisabled();
    }
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  private startTimeout() {
    if (!this.enabled()) return;

    const control = this.form.get('item');
    if (this.offTimeOut()) {
      if (control?.invalid) {
        this.getStyleError();
      } else {
        this.offTimeOut.set(false);
      }
      return;
    }

    clearTimeout(this.timeoutHandle);
    this.offTimeOut.set(false);

    this.timeoutHandle = setTimeout(() => {
      const currentControl = this.form.get('item');
      if (currentControl && currentControl.invalid) {
        this.offTimeOut.set(true);
        this.getStyleError();
      }
    }, this.fieldErrorTimeout.delayMs(this.timeoutSeconds));
  }

  private emitFieldStateChange(): void {
    const current = this.getCurrentValue();
    this.fieldStateChange.emit(current);
  }

  getCurrentValue(): { formName: string | undefined; value: string | number | null; valid: boolean } {
    const control = this.form.get('item');
    let value: string | number | null = control?.value ?? null;

    if (value === '') {
      value = null;
    }

    if (this.configSignal().type === 'number' && value !== null && value !== '') {
      value = value.toString().replace(/,/g, '');
    }

    return {
      formName: this.configSignal().formName,
      value,
      valid: !!control?.valid,
    };
  }

  forceShowValidation(): void {
    this.offTimeOut.set(true);
    this.getStyleError();
  }

  emit() {
    let valueToEmit = this.form.value.item;
    const control = this.form.get('item');
    const isValid = control ? control.valid : false;
    const formName = this.configSignal().formName || '';

    if (this.configSignal().type === 'number' && valueToEmit) {
      valueToEmit = valueToEmit.toString().replace(/,/g, '');
    }

    const hasValueChanged = valueToEmit !== this.lastEmittedValue();

    if ((this.form.valid || valueToEmit == '') && hasValueChanged) {
      this.lastEmittedValue.set(valueToEmit);
      this.submitValue.emit({
        formName: formName,
        value: valueToEmit,
        isValid: isValid,
        isDirty: valueToEmit !== this.configSignal().originalValue
      });
    }

    if (!this.form.valid && this.enabled()) {
      this.lastEmittedValue.set(valueToEmit);
      this.submitValue.emit({
        formName: formName,
        value: null,
        isValid: false,
        isDirty: true
      });
    }
  }

  resetDebounce() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.emit();
    }, 700);
  }

  onBlurInput() {
    this.emit();

    const control = this.form.get('item');
    if (control?.invalid) {
      this.forceShowValidation();
    }

    if (this.emitOnlyOnBlur()) {
      clearTimeout(this.debounceTimer);
    }
    this.emitFieldStateChange();
  }

  private formatNumberWithCommas(value: string): string {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  onUserInput(): void {
    this.editedByUser = true;
  }

  onNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const raw = input.value;

    if (/[^\d.,\s]/.test(raw)) {
      input.value = this.formattedValue();
      return;
    }

    this.editedByUser = true;
    const digits = raw.replace(/[.,\s]/g, '');
    const formatted = digits ? this.formatNumberWithCommas(digits) : '';
    this.formattedValue.set(formatted);
    this.valueSignal.set(digits);
    input.value = formatted;
    this.form.get('item')?.setValue(digits);
  }

  private updateStylesByState() {
    const control = this.form.get('item');
    if (!control) return;

    if (!this.enabled()) {
      this.getStyleTextDisabled();
      return;
    }

    if (control.invalid && this.offTimeOut()) {
      this.getStyleError();
      return;
    }

    this.getStyleEnabled();
  }

  getStyleError() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error');
      }
    });
  }

  getStyleEnabled() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled');
      }
    });
  }

  getStyleTextDisabled() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled');
      }
    });
  }
}
